import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  Input,
  Spinner,
  Table,
} from "reactstrap";
import { postMethod } from "../../services/httpServices";

const defaultEntry = {
  categoryCode: "",
  categoryName: "",
  subCategoryCode: "",
  subCategoryName: "",
  gstEligible: false,
};

const AddCategory = ({ closeModal }) => {
  const [entries, setEntries] = useState([{ ...defaultEntry }]);
  const [errors, setErrors] = useState([{}]);
  const [loading, setLoading] = useState(false);

  const handleChange = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);

    // Clear validation error on change
    const updatedErrors = [...errors];
    if (updatedErrors[index]) {
      updatedErrors[index][field] = "";
      setErrors(updatedErrors);
    }
  };

  const addRow = () => {
    setEntries([...entries, { ...defaultEntry }]);
    setErrors([...errors, {}]);
  };

  const removeRow = (index) => {
    const updatedEntries = [...entries];
    const updatedErrors = [...errors];
    updatedEntries.splice(index, 1);
    updatedErrors.splice(index, 1);
    setEntries(updatedEntries);
    setErrors(updatedErrors);
  };

  const validate = () => {
    const newErrors = entries.map((entry) => {
      const entryErrors = {};
      if (!entry.categoryCode) entryErrors.categoryCode = "Required";
      if (!entry.categoryName) entryErrors.categoryName = "Required";
      if (!entry.subCategoryCode) entryErrors.subCategoryCode = "Required";
      if (!entry.subCategoryName) entryErrors.subCategoryName = "Required";
      return entryErrors;
    });
    setErrors(newErrors);
    return newErrors.every((e) => Object.keys(e).length === 0);
  };

  const submit = async () => {
    if (!validate()) return;

    setLoading(true);

    const payload = entries.map((entry) => ({
      ...entry,
      id: 0,
      createdAt: new Date().toISOString(),
      createdBy: "system",
      updatedAt: new Date().toISOString(),
      updatedBy: "system",
      isDeleted: false,
      isBlocked: false,
    }));

    try {
      const res = await postMethod(
        "/api/Inventory/AddCategories",
        payload,
        "post"
      );
      if (res?.data?.responseCode === "200") {
        setEntries([{ ...defaultEntry }]);
        setErrors([{}]);
        alert("Categories submitted successfully!");
      } else {
        alert(res?.data?.responseMessage || "Failed to submit.");
      }
    } catch (error) {
      alert("Something went wrong while submitting.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-secondary shadow">
      <CardHeader className="bg-white border-0 d-flex justify-content-between">
        <h3 className="mb-0">Add Category</h3>
        {closeModal && <Button close onClick={closeModal} />}
      </CardHeader>
      <CardBody>
        <Form>
          <Table bordered responsive className="align-middle">
            <thead className="thead-light">
              <tr>
                <th>Category Code</th>
                <th>Category Name</th>
                <th>SubCategory Code</th>
                <th>SubCategory Name</th>
                <th>GST Eligible</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr key={idx}>
                  <td>
                    <Input
                      type="text"
                      value={entry.categoryCode}
                      className="form-control-alternative"
                      onChange={(e) =>
                        handleChange(idx, "categoryCode", e.target.value)
                      }
                      invalid={!!errors[idx]?.categoryCode}
                    />
                    {errors[idx]?.categoryCode && (
                      <div className="text-danger text-sm">
                        {errors[idx].categoryCode}
                      </div>
                    )}
                  </td>
                  <td>
                    <Input
                      type="text"
                      value={entry.categoryName}
                      className="form-control-alternative"
                      onChange={(e) =>
                        handleChange(idx, "categoryName", e.target.value)
                      }
                      invalid={!!errors[idx]?.categoryName}
                    />
                    {errors[idx]?.categoryName && (
                      <div className="text-danger text-sm">
                        {errors[idx].categoryName}
                      </div>
                    )}
                  </td>
                  <td>
                    <Input
                      type="text"
                      value={entry.subCategoryCode}
                      className="form-control-alternative"
                      onChange={(e) =>
                        handleChange(idx, "subCategoryCode", e.target.value)
                      }
                      invalid={!!errors[idx]?.subCategoryCode}
                    />
                    {errors[idx]?.subCategoryCode && (
                      <div className="text-danger text-sm">
                        {errors[idx].subCategoryCode}
                      </div>
                    )}
                  </td>
                  <td>
                    <Input
                      type="text"
                      value={entry.subCategoryName}
                      className="form-control-alternative"
                      onChange={(e) =>
                        handleChange(idx, "subCategoryName", e.target.value)
                      }
                      invalid={!!errors[idx]?.subCategoryName}
                    />
                    {errors[idx]?.subCategoryName && (
                      <div className="text-danger text-sm">
                        {errors[idx].subCategoryName}
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    <Input
                      type="checkbox"
                      checked={entry.gstEligible}
                      onChange={(e) =>
                        handleChange(idx, "gstEligible", e.target.checked)
                      }
                    />
                  </td>
                  <td className="text-center">
                    {entries.length > 1 && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeRow(idx)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between mt-3">
            <Button color="info" onClick={addRow}>
              <i className="fas fa-plus"></i>{" "}
            </Button>
            <Button color="success" onClick={submit} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Submit"}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default AddCategory;
